import {
  Box,
  ButtonBase,
  TableCell,
  TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  BulkActionBar,
  BulkButton,
  BulkCheckbox,
  BulkMenuButton,
  useBulkSelection,
} from "../../../components/bulk-selection/index.js";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog.js";
import { supportApi } from "../client.js";
import { HeaderAction } from "../HeaderActionSlot.js";
import { SupportErrorState } from "../components/SupportEmptyState.js";
import { Pagination } from "../../../components/ui/pagination.js";
import { SupportTable } from "../components/SupportTable.js";
import { PageLoader } from "../../../components/ui/page-loader.js";
import { NewTicketModal } from "./components/NewTicketModal.js";
import { SortableTh } from "./ticket-list/SortableTh.js";
import { TicketFilters } from "./ticket-list/TicketFilters.js";
import { TicketRow } from "./ticket-list/TicketRow.js";
import {
  nextSort,
  prioritySlug,
  type SortDir,
  type SortKey,
} from "./ticket-list/ticketListConsts.js";

/**
 * Admin ticket list. Search, status, priority, assignee, customer, and
 * sort state all live in the URL so a shared link reproduces the exact
 * view. Bulk-action toolbar (delete / change status) sits on top of the
 * shared `useBulkSelection` hook — same behaviour any other table in
 * the app gets when it imports from `components/bulk-selection`.
 * Shift-click extends the selection over a range; toggle-all on the
 * header checkbox covers everything on the visible page.
 *
 * **Endpoint:** this page calls `GET /api/admin/tickets` (the
 * staff-admin index added by Task 12 of the 2026-05-13 ticket-system
 * completion plan), not the legacy `/api/support/tickets`. The admin
 * endpoint returns a flatter row shape (`requester_name`,
 * `assignee_name`, `sla_status`, `last_activity_at`) and a
 * `meta: { page, per_page, total }` envelope. Filter chips send slug
 * values — `status=open`, `priority=high` — because the admin
 * controller filters by slug, not numeric id. Bulk-action endpoints
 * remain under `/api/support/tickets/bulk_*` (proxied via
 * `supportApi.bulk*` below); they were not duplicated into the admin
 * namespace.
 *
 * This file is the orchestrator: the URL filter/sort state, the lookup
 * queries, the bulk mutations, and the table assembly. The filter bar lives
 * in `ticket-list/TicketFilters`, each row in `TicketRow`, the sortable
 * header in `SortableTh`, and the sort/slug helpers in `ticketListConsts`.
 */
export function TicketListPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();

  const q = params.get("q") ?? undefined;
  const status = params.get("status") ?? undefined;
  const assignee = params.get("assignee_id") ?? undefined;
  const priority = params.get("priority") ?? undefined;
  const organization = params.get("organization_id") ?? undefined;
  const page = params.get("page") ?? "1";
  const perPage = params.get("per_page") ?? "25";
  const sort = (params.get("sort") as SortKey | null) ?? "updated_at";
  const dir = (params.get("dir") as SortDir | null) ?? "desc";

  const ticketsQuery = useQuery({
    queryKey: [
      "support",
      "admin-tickets",
      { q, status, assignee, priority, organization, page, perPage, sort, dir },
    ],
    queryFn: () => {
      const p: Record<string, string> = { page, per_page: perPage, sort, dir };
      if (q) p.q = q;
      if (status) p.status = status;
      if (assignee) p.assignee_id = assignee;
      if (priority) p.priority = priority;
      if (organization) p.organization_id = organization;
      return supportApi.listAdminTickets(p);
    },
    staleTime: 15_000,
  });

  const statusesQuery = useQuery({
    queryKey: ["support", "statuses"],
    queryFn: supportApi.listStatuses,
    staleTime: 5 * 60_000,
  });
  const prioritiesQuery = useQuery({
    queryKey: ["support", "priorities"],
    queryFn: supportApi.listPriorities,
    staleTime: 5 * 60_000,
  });
  const assigneesQuery = useQuery({
    queryKey: ["support", "assignable"],
    queryFn: () => supportApi.listAssignableUsers(),
    staleTime: 5 * 60_000,
  });
  // Customer filter chip — first 50 orgs by name. The admin index
  // filters by `organization_id` (the controller aliases the customer
  // selector onto it), so we feed the chip with the same dataset the
  // Organizations tab uses.
  const organizationsQuery = useQuery({
    queryKey: ["support", "organizations", "chip"],
    queryFn: () => supportApi.listOrganizations({ per_page: "50" }),
    staleTime: 5 * 60_000,
  });

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("page"); // reset paging on any filter / sort change
    setParams(next, { replace: true });
  }

  function setSort(clicked: SortKey) {
    const { sort: s, dir: d } = nextSort(sort, dir, clicked);
    const next = new URLSearchParams(params);
    next.set("sort", s);
    next.set("dir", d);
    next.delete("page");
    setParams(next, { replace: true });
  }

  // ── Bulk selection (shared hook — shift-click range, toggle-all,
  // auto-prune on page changes). ────────────────────────────────────
  const tickets = ticketsQuery.data?.data ?? [];
  const bulk = useBulkSelection(tickets);

  const bulkDestroy = useMutation({
    mutationFn: (ids: number[]) => supportApi.bulkDestroyTickets(ids),
    onSuccess: () => {
      bulk.clear();
      qc.invalidateQueries({ queryKey: ["support", "admin-tickets"] });
    },
  });
  const bulkTransition = useMutation({
    mutationFn: ({ ids, statusId }: { ids: number[]; statusId: number }) =>
      supportApi.bulkTransitionTickets(ids, statusId),
    onSuccess: () => {
      bulk.clear();
      qc.invalidateQueries({ queryKey: ["support", "admin-tickets"] });
    },
  });

  const bulkPending = bulkDestroy.isPending || bulkTransition.isPending;
  const [newOpen, setNewOpen] = useState(false);
  // Bulk-delete confirmation now routes through <ConfirmDialog> (the
  // sanctioned destructive-action path — window.confirm is forbidden,
  // and unstubbable in Cypress).
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  // Derive paging numbers from the admin envelope's flat shape.
  const meta = ticketsQuery.data?.meta;
  const totalCount = meta?.total ?? 0;
  const currentPage = meta?.page ?? Number(page);
  const totalPages = meta ? Math.max(1, Math.ceil(meta.total / meta.per_page)) : 1;

  return (
    <Box component="section" sx={{ display: "grid", gap: 2 }}>
      <HeaderAction>
        <ButtonBase
          onClick={() => setNewOpen(true)}
          sx={{
            display: "inline-flex",
            height: 36,
            alignItems: "center",
            gap: 0.75,
            borderRadius: 999,
            bgcolor: "primary.main",
            px: 2,
            fontSize: 14,
            fontWeight: 500,
            color: "primary.contrastText",
            "&:hover": { filter: "brightness(1.1)" },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          New ticket
        </ButtonBase>
      </HeaderAction>

      <TicketFilters
        q={q ?? ""}
        onSearch={(v) => setParam("q", v || null)}
        status={status ?? ""}
        // Status slug — the admin index filters by slug, not id.
        statusOptions={
          statusesQuery.data?.data.map((s) => ({ value: s.slug, label: s.name })) ?? []
        }
        priority={priority ?? ""}
        priorityOptions={
          prioritiesQuery.data?.data.map((p) => ({
            value: prioritySlug(p.name, (p as unknown as { slug?: string }).slug),
            label: p.name,
          })) ?? []
        }
        assignee={assignee ?? ""}
        assigneeOptions={
          assigneesQuery.data?.data.map((a) => ({
            value: String(a.id),
            label: [a.first_name, a.last_name].filter(Boolean).join(" ") || a.email,
          })) ?? []
        }
        organization={organization ?? ""}
        organizationOptions={
          organizationsQuery.data?.data.map((o) => ({
            value: String(o.id),
            label: o.name,
          })) ?? []
        }
        onParam={setParam}
      />

      <BulkActionBar count={bulk.count} onClear={bulk.clear}>
        <BulkMenuButton
          label="Change status…"
          disabled={bulkPending}
          items={
            statusesQuery.data?.data.map((s) => ({
              id: s.id,
              label: s.name,
              onSelect: () =>
                bulkTransition.mutate({
                  ids: [...bulk.selected],
                  statusId: s.id,
                }),
            })) ?? []
          }
        />
        <BulkButton
          variant="destructive"
          disabled={bulkPending}
          onClick={() => setConfirmBulkDelete(true)}
        >
          Delete
        </BulkButton>
      </BulkActionBar>

      <ConfirmDialog
        isOpen={confirmBulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
        title={`Delete ${bulk.count} ticket${bulk.count === 1 ? "" : "s"}?`}
        description="This soft-deletes — they can be restored from the audit log."
        confirmLabel={`Delete ${bulk.count} ticket${bulk.count === 1 ? "" : "s"}`}
        onConfirm={async () => {
          await bulkDestroy.mutateAsync([...bulk.selected]);
        }}
      />

      {ticketsQuery.isLoading && <PageLoader />}
      {ticketsQuery.isError && (
        <SupportErrorState
          error={ticketsQuery.error}
          resourceLabel="tickets"
        />
      )}

      {ticketsQuery.data && (
        <SupportTable
          head={
            <TableRow>
              <TableCell sx={{ width: 40 }} align="left">
                <BulkCheckbox
                  checked={bulk.allOnPageChecked}
                  indeterminate={bulk.someOnPageChecked}
                  onChange={(checked) => bulk.toggleAllOnPage(checked)}
                  ariaLabel="Select all rows on this page"
                />
              </TableCell>
              <SortableTh
                col="reference"
                activeKey={sort}
                activeDir={dir}
                onSort={setSort}
                sx={{ width: 110 }}
              />
              <SortableTh col="title"    activeKey={sort} activeDir={dir} onSort={setSort} />
              <TableCell align="left">Status</TableCell>
              <SortableTh col="priority" activeKey={sort} activeDir={dir} onSort={setSort} />
              <TableCell align="left">Requester</TableCell>
              <TableCell align="left">Assignee</TableCell>
              <TableCell align="left">SLA</TableCell>
              <SortableTh col="updated"  activeKey={sort} activeDir={dir} onSort={setSort} />
            </TableRow>
          }
          empty={
            ticketsQuery.data.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6, color: "text.disabled" }}>
                  No tickets match these filters.
                </TableCell>
              </TableRow>
            ) : null
          }
        >
          {ticketsQuery.data.data.map((t) => (
            <TicketRow
              key={t.id}
              ticket={t}
              isChecked={bulk.isSelected(t.id)}
              onSelect={(checked, opts) =>
                bulk.setChecked(t.id, checked, { shiftKey: opts.shiftKey })
              }
            />
          ))}
        </SupportTable>
      )}

      {ticketsQuery.data?.meta && (
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          perPage={Number(perPage)}
          entityNoun="tickets"
          onPageChange={(p) => setParam("page", String(p))}
          onPerPageChange={(n) => {
            // Switching page size: drop the page param so the user
            // doesn't land on a now-out-of-range page.
            const next = new URLSearchParams(params);
            next.set("per_page", String(n));
            next.delete("page");
            setParams(next, { replace: true });
          }}
        />
      )}

      {newOpen && <NewTicketModal onClose={() => setNewOpen(false)} />}
    </Box>
  );
}
